import React, { useEffect, useRef } from "react";
import {
  ShieldCheck, LockKeyhole, Eye, Database, Cookie, Share2, UserRound,
  Settings, Mail, FileText, ChevronRight, CheckCircle2, ArrowUpRight
} from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const sections = [
  {
    id: "information", number: "01", icon: Database, title: "Information We Collect",
    paragraphs: [
      "Beyond the Analysis may collect information that you voluntarily provide when you interact with our website, request a consultation, submit a contact form, or communicate with our team.",
      "We aim to collect only information that is reasonably relevant to providing our services and operating our website."
    ],
    bullets: [
      "Contact information, such as your name and email address.",
      "Business information you choose to share when discussing a project or our services.",
      "Messages, enquiries, feedback, and other information you submit through our website.",
      "Technical information, such as browser type, device information, IP address, and website usage data, where collected."
    ]
  },
  {
    id: "usage", number: "02", icon: Eye, title: "How We Use Your Information",
    paragraphs: [
      "We may use the information we collect to:",
      "We do not intend to use personal information for purposes that are incompatible with the reasons it was collected, unless permitted by applicable law."
    ],
    bullets: [
      "Respond to your questions and service enquiries.",
      "Arrange consultations and discuss potential projects.",
      "Provide, maintain, and improve our website and services.",
      "Understand website performance and visitor engagement.",
      "Send relevant business communications when permitted.",
      "Protect our website and prevent misuse or fraudulent activity.",
      "Meet applicable legal and regulatory obligations."
    ]
  },
  {
    id: "cookies", number: "03", icon: Cookie, title: "Cookies & Analytics",
    paragraphs: [
      "Our website may use cookies and similar technologies to support essential website functions, understand visitor behaviour, and improve the browsing experience.",
      "Depending on the tools enabled on our website, these technologies may:",
      "You can manage cookies through your browser settings. Where required by law, we will request consent before using non-essential cookies. Disabling certain cookies may affect some website features."
    ],
    bullets: [
      "Remember preferences and settings.",
      "Help us understand traffic and website performance.",
      "Measure the effectiveness of marketing campaigns.",
      "Support website functionality and security."
    ]
  },
  {
    id: "sharing", number: "04", icon: Share2, title: "Sharing of Information",
    paragraphs: [
      "We do not sell personal information as part of our ordinary business practices. We may share information in limited circumstances, such as:",
      "Third-party providers may process information under their own privacy terms and applicable contractual safeguards."
    ],
    bullets: [
      "Service providers: Hosting, analytics, communication, security, and other providers that help operate our business.",
      "Business operations: Where sharing is necessary to deliver a service you have requested.",
      "Legal requirements: When disclosure is required by law or necessary to respond to valid legal processes.",
      "Business protection: Where reasonably necessary to protect our rights, users, systems, or property."
    ]
  },
  {
    id: "security", number: "05", icon: LockKeyhole, title: "Data Security & Retention",
    paragraphs: [
      "We use reasonable technical and organisational measures designed to protect personal information against unauthorised access, loss, alteration, or disclosure.",
      "No method of internet transmission or electronic storage can be guaranteed to be completely secure. We therefore cannot promise absolute security.",
      "We retain personal information only for as long as reasonably necessary for the purposes described in this policy, including service delivery, recordkeeping, dispute resolution, and legal compliance."
    ]
  },
  {
    id: "rights", number: "06", icon: UserRound, title: "Your Privacy Rights",
    paragraphs: [
      "Depending on your location and applicable law, you may have rights relating to your personal information, including the right to:",
      "To exercise an applicable right, contact us using the details provided below. We may need to verify your identity before responding."
    ],
    bullets: [
      "Request access to personal information we hold about you.",
      "Ask us to correct inaccurate or incomplete information.",
      "Request deletion of information, subject to legal exceptions.",
      "Object to or restrict certain processing activities.",
      "Withdraw consent where processing relies on your consent.",
      "Opt out of marketing communications."
    ]
  },
  {
    id: "third-parties", number: "07", icon: ArrowUpRight, title: "Third-Party Websites",
    paragraphs: [
      "Our website may contain links to third-party websites, platforms, or services. These destinations are operated independently and may have their own privacy policies and practices.",
      "We encourage you to review their privacy notices before submitting personal information. We are not responsible for the privacy practices of third-party websites that we do not control."
    ]
  },
  {
    id: "children", number: "08", icon: ShieldCheck, title: "Children's Privacy",
    paragraphs: [
      "Our website and business services are intended for a general business audience and are not directed at children.",
      "If you believe a child has provided personal information to us, please contact us so that we can assess the request and take appropriate action in accordance with applicable law."
    ]
  },
  {
    id: "changes", number: "09", icon: FileText, title: "Changes to This Policy",
    paragraphs: [
      "We may update this Privacy Policy when our practices, services, technology, or legal obligations change.",
      "Any revised version will be published on this page with an updated revision date. We encourage you to review this page periodically."
    ]
  }
];

const styles = {
  page: { position: "relative", overflow: "hidden", background: "#080d13", color: "#e5e7eb", minHeight: "100vh", fontFamily: "Inter, system-ui, sans-serif", padding: "0 24px 80px" },
  background: { position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(163,230,53,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(163,230,53,.035) 1px, transparent 1px)", backgroundSize: "54px 54px", maskImage: "linear-gradient(to bottom, black, transparent 75%)" },
  container: { width: "100%", maxWidth: 1180, margin: "0 auto", position: "relative", zIndex: 1 },
  hero: { display: "grid", gridTemplateColumns: "minmax(0, 1.25fr) minmax(260px, .75fr)", gap: 50, alignItems: "center", padding: "100px 0 70px" },
  eyebrow: { display: "inline-flex", alignItems: "center", gap: 9, color: "#a3e635", fontSize: 12, fontWeight: 800, letterSpacing: "2px", marginBottom: 22 },
  h1: { fontSize: "clamp(42px, 6vw, 76px)", lineHeight: 1.04, letterSpacing: "-3px", margin: "0 0 24px", color: "#f8fafc" },
  gradient: { color: "#a3e635" },
  lead: { fontSize: 17, lineHeight: 1.85, color: "#a1aab8", maxWidth: 650, margin: "0 0 25px" },
  meta: { display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", color: "#b8c0ca", fontSize: 13 },
  visual: { display: "grid", placeItems: "center", minHeight: 300, position: "relative" },
  shield: { width: 210, height: 210, borderRadius: "50%", display: "grid", placeItems: "center", color: "#a3e635", border: "1px solid rgba(163,230,53,.4)", boxShadow: "0 0 0 22px rgba(163,230,53,.035), 0 0 0 44px rgba(163,230,53,.025), 0 0 70px rgba(163,230,53,.12)", background: "radial-gradient(circle, rgba(163,230,53,.12), rgba(8,13,19,.2) 68%)" },
  floating: { position: "absolute", display: "flex", alignItems: "center", gap: 9, padding: "12px 15px", border: "1px solid #27323d", borderRadius: 12, background: "#111923", color: "#dce4ec", fontSize: 13, boxShadow: "0 12px 40px #0005" },
  layout: { display: "grid", gridTemplateColumns: "270px minmax(0, 1fr)", gap: 28, alignItems: "start" },
  sidebar: { position: "sticky", top: 24, border: "1px solid #26313d", borderRadius: 18, padding: 22, background: "#0e151e" },
  sidebarLabel: { display: "block", color: "#a3e635", fontSize: 11, fontWeight: 800, letterSpacing: "1.8px", marginBottom: 17 },
  navLink: { display: "flex", alignItems: "center", gap: 9, textDecoration: "none", color: "#b6c0cc", padding: "10px 0", fontSize: 13, borderBottom: "1px solid #202a34" },
  content: { minWidth: 0 },
  intro: { display: "flex", gap: 16, padding: 24, borderRadius: 18, border: "1px solid #34432b", background: "linear-gradient(120deg, rgba(163,230,53,.09), #101720 70%)", marginBottom: 18 },
  card: { padding: "28px 30px", marginBottom: 16, border: "1px solid #26313d", borderRadius: 18, background: "linear-gradient(135deg, #111923, #0c121a)", scrollMarginTop: 24 },
  sectionHead: { display: "flex", alignItems: "center", gap: 14, marginBottom: 20 },
  iconBox: { width: 46, height: 46, flexShrink: 0, borderRadius: 13, display: "grid", placeItems: "center", color: "#a3e635", background: "rgba(163,230,53,.1)", border: "1px solid rgba(163,230,53,.2)" },
  sectionNo: { fontSize: 10, color: "#a3e635", fontWeight: 800, letterSpacing: "1.6px" },
  h2: { margin: "5px 0 0", color: "#f3f6f9", fontSize: 22, letterSpacing: "-.4px" },
  body: { color: "#aab4c1", fontSize: 14, lineHeight: 1.9 },
  list: { paddingLeft: 22, margin: "12px 0 18px" },
  contact: { display: "flex", gap: 18, alignItems: "flex-start", padding: 28, border: "1px solid rgba(163,230,53,.3)", borderRadius: 18, background: "linear-gradient(120deg, rgba(163,230,53,.12), #101720 72%)", marginTop: 24 },
  contactLink: { display: "inline-flex", gap: 8, alignItems: "center", color: "#a3e635", textDecoration: "none", fontWeight: 700, marginTop: 8 },
  note: { color: "#7f8b99", fontSize: 12, lineHeight: 1.7, marginTop: 20 },
  back: { display: "inline-flex", alignItems: "center", gap: 8, color: "#a3e635", textDecoration: "none", marginTop: 24, fontSize: 14 }
};

export default function PrivacyPolicy() {
  const pageRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(".pp-hero-item", { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: .7, stagger: .12, ease: "power3.out" });
      gsap.utils.toArray(".pp-section").forEach((section) => {
        gsap.fromTo(section, { opacity: 0, y: 18 }, {
          opacity: 1, y: 0, duration: .55, ease: "power2.out",
          scrollTrigger: { trigger: section, start: "top 90%", once: true }
        });
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} style={styles.page}>
      <div aria-hidden="true" style={styles.background} />
      <div style={styles.container}>
        <section style={styles.hero}>
          <div>
            <div className="pp-hero-item" style={styles.eyebrow}><ShieldCheck size={16} /> YOUR PRIVACY MATTERS</div>
            <h1 className="pp-hero-item" style={styles.h1}>Privacy, with <span style={styles.gradient}>purpose.</span></h1>
            <p className="pp-hero-item" style={styles.lead}>Transparency is the foundation of trust. Learn how Beyond the Analysis collects, uses, and protects your information when you interact with our website.</p>
            <div className="pp-hero-item" style={styles.meta}><span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><CheckCircle2 size={16} color="#a3e635" /> Clear and transparent practices</span><span>Last updated: October 6, 2026</span></div>
          </div>
          <div className="pp-hero-item" style={styles.visual}>
            <div style={styles.shield}><ShieldCheck size={88} strokeWidth={1.2} /></div>
            <div style={{ ...styles.floating, top: "18%", right: 0 }}><LockKeyhole size={17} color="#a3e635" /> Data protection</div>
            <div style={{ ...styles.floating, bottom: "16%", left: 0 }}><Eye size={17} color="#a3e635" /> Transparency first</div>
          </div>
        </section>

        <div className="pp-layout" style={styles.layout}>
          <aside style={styles.sidebar}>
            <span style={styles.sidebarLabel}>ON THIS PAGE</span>
            <nav aria-label="Privacy policy sections">
              {sections.map((section) => (
                <a key={section.id} href={`#${section.id}`} style={styles.navLink}>
                  <span style={{ color: "#a3e635", fontSize: 11 }}>{section.number}</span>
                  <span style={{ flex: 1 }}>{section.title}</span>
                  <ChevronRight size={14} />
                </a>
              ))}
            </nav>
            <p style={{ color: "#7f8b99", fontSize: 12, lineHeight: 1.7, margin: "18px 0 0" }}><LockKeyhole size={16} color="#a3e635" style={{ verticalAlign: "middle", marginRight: 7 }} />Your information deserves responsible handling at every step.</p>
          </aside>

          <div style={styles.content}>
            <section style={styles.intro}>
              <div style={styles.iconBox}><FileText size={23} /></div>
              <div><h2 style={{ ...styles.h2, marginTop: 0 }}>About this policy</h2><p style={{ ...styles.body, marginBottom: 0 }}>This Privacy Policy explains how Beyond the Analysis handles personal information in connection with our website and business communications. The information we actually collect depends on the features and services you use.</p></div>
            </section>

            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <section key={section.id} id={section.id} className="pp-section" style={styles.card}>
                  <div style={styles.sectionHead}>
                    <div style={styles.iconBox}><Icon size={21} /></div>
                    <div><span style={styles.sectionNo}>SECTION {section.number}</span><h2 style={styles.h2}>{section.title}</h2></div>
                  </div>
                  <div style={styles.body}>
                    {section.paragraphs.map((paragraph, index) => (
                      <React.Fragment key={paragraph}>
                        <p style={{ margin: "0 0 13px" }}>{paragraph}</p>
                        {section.bullets && index === 0 && (
                          <ul style={styles.list}>
                            {section.bullets.map((bullet) => {
                              const splitAt = bullet.indexOf(": ");
                              return <li key={bullet} style={{ paddingLeft: 3, marginBottom: 8 }}>{splitAt > -1 && ["Service providers", "Business operations", "Legal requirements", "Business protection"].includes(bullet.slice(0, splitAt)) ? <><strong style={{ color: "#e5e7eb" }}>{bullet.slice(0, splitAt)}:</strong>{bullet.slice(splitAt + 1)}</> : bullet}</li>;
                            })}
                          </ul>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </section>
              );
            })}

            <section style={styles.contact}>
              <div style={styles.iconBox}><Mail size={25} /></div>
              <div>
                <span style={styles.sectionNo}>HAVE A QUESTION?</span>
                <h2 style={styles.h2}>Let's talk about your privacy.</h2>
                <p style={styles.body}>For privacy-related questions or requests, contact our team using the email address below.</p>
                <a href="mailto:riz@beyondtheanalysis.com" style={styles.contactLink}>riz@beyondtheanalysis.com <ArrowUpRight size={16} /></a>
              </div>
            </section>

            <p style={styles.note}><Settings size={15} style={{ verticalAlign: "middle", marginRight: 7 }} />This policy should be reviewed against your actual data practices and the privacy laws applicable to your business before publication.</p>
            <a href="/" style={styles.back}><ChevronRight size={16} style={{ transform: "rotate(180deg)" }} /> Back to home</a>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 850px) {
          .pp-layout { grid-template-columns: 1fr !important; }
          .pp-layout aside { position: static !important; }
        }
        @media (max-width: 650px) {
          .pp-layout { gap: 18px !important; }
          .pp-layout + * { min-width: 0; }
          main[style] { padding-left: 16px !important; padding-right: 16px !important; }
          main[style] section[style*="grid-template-columns"] { grid-template-columns: 1fr !important; gap: 22px !important; padding-top: 65px !important; }
          main[style] section[style*="grid-template-columns"] > div:last-child { min-height: 240px !important; }
          main[style] section[style*="grid-template-columns"] h1 { letter-spacing: -1.8px !important; }
          main[style] section[style*="grid-template-columns"] > div:last-child > div[style*="position: absolute"] { font-size: 11px !important; }
          main[style] section[style*="padding: 28px 30px"] { padding: 22px 18px !important; }
          main[style] section[style*="align-items: flex-start"] { padding: 22px 18px !important; }
        }
      `}</style>
    </main>
  );
}
